import React, { useState, type FC, type MouseEvent } from 'react'
import RecordTable from '../../components/records-table'
import InputBox from '../../components/inputbox'
import type { AssetRecordDetailsProps } from './utils/types'
import ActionButton from '../../components/action-button'
import closeIcon from '../../utility/img/cancel.png'
import ConfirmationModal from '../../components/confirm-popup'
import { useAssetDetails } from './hooks/useAssetDetails'
import { TRADER_INPUT_FIELDS_MAP } from '../../utility/constant'
import GenericDropdown from '../../components/dropdown'
import Message from '../../components/message'
import MiniNotepad from '../../components/notepad'
import ShowNotes from '../../components/show-notes'
import { formatInputToDollar, getCurrencyPrefix } from '../../utility/formatting'
import ReconcileSummaryList from './reconcile-components/ReconcileSummaryList'
import ReconcileDetails from './reconcile-components/ReconcileDetails'

const AssetBucketsPopUp: FC<AssetRecordDetailsProps> = ({
  show,
  onClose,
  batchId,
  batchUniqueId,
  assetType,
  setMessage,
  setMessageColor,
  updateError,
  status,
  basketOverall,
  fundingRequestCode,
  ttlNotional,
  ttlQuantity,
  currency,
  reconcileIndicator,
  reconcileCount,
  execScheme
}) => {
  const [localNotional, setLocalNotional] = useState(ttlNotional)
  const [localQuantity, setLocalQuantity] = useState(ttlQuantity)
  const [isReconcilePopupOpen, setIsReconcilePopupOpen] = useState(false)
  const [selectedRecordIndex, setSelectedRecordIndex] = useState<number>(0)
  const [isInEditMode, setIsInEditMode] = useState(false)

  const isRejected = status === 'TRADER-REJECTED' || status === 'RISK-REJECTED'
  const isPreApproved = status === 'TRADER-PREAPPROVED' || status === 'TRADER-PREAPPROVED-EXCEPTION'
  // ASSET-LOADED is view-only: open is allowed but no editing or status actions, just Close
  const isAssetLoaded = status === 'ASSET-LOADED'

  const {
    records,
    setRecords,
    inputData,
    setInputData,
    isDataReady,
    loading,
    error,
    QueryWithExtendedTimeComponent,
    isConfirmationDialogActive,
    setIsConfirmationDialogActive,
    isRemoveRecordModalOpen,
    setIsRemoveRecordModalOpen,
    handleRecordRemoval,
    confirmRecordRemoval,
    dataChanged,
    ableToProceed,
    isMutating,
    isSecondaryMutating,
    FRDropDownOptions,
    execSchemeOptions,
    selectedExecScheme,
    editableField,
    handleDiscard,
    handleCloseIconClick,
    selectedFRCode,
    handleDropdownChange,
    handleExecSchemeChange,
    handlePrimaryActionWithValidation,
    handleCancelClick,
    getPrimaryLabel,
    shouldShowPreApproveButtons,
    shouldShowPreApproveException,
    shouldShowRejectButton,
    recalcMessage,
    recalcMessageColor,
    setRecalcMessage,
    setRecalcMessageColor,
    traderNote,
    setTraderNote,
    displayingNotes,
    setHasFlag,
    reconciliationResultData,
    loadingReconciliation,
    errorReconciliation,
    handleRejectClick,
    portfolioReadOnly,
    riskOverall,
    handleSaveRecalc,
    reconcileStatus,
    hasDataChanges,
    holidayAsWarning,
    dateException,
    setDateException,
    basketInstrumentCurrency,
    basketInstrumentIssue
  } = useAssetDetails(
    {
      batchId,
      batchUniqueId,
      assetType,
      setMessage,
      setMessageColor,
      status,
      onClose,
      basketOverall,
      updateError,
      testDetails: true,
      reconcileIndicator,
      reconcileCount
    }
  )

  // Disable edit in compliance states, when in RISK* with linked FR, when rejected on pre-approve-only path (reconcileIndicator === 1), or when ASSET-LOADED (view-only)
  const disableEdit: boolean = isAssetLoaded || /(COMPL)/.test(status) || (/(RISK)/.test(status) && !!fundingRequestCode) || (isRejected && reconcileIndicator === 1 && !shouldShowPreApproveButtons)

  if (!show) return null

  // Handle all loading and error states in one place
  if (loading || (error ?? loadingReconciliation) || errorReconciliation) {
    if (loading || error) {
      return <QueryWithExtendedTimeComponent />
    }
    if (loadingReconciliation) {
      return (
        <div className="flex items-center justify-center p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading reconciliation data...</p>
          </div>
        </div>
      )
    }
    if (errorReconciliation) {
      return (
        <div className="flex items-center justify-center p-4">
          <div className="text-center text-red-600">
            <p className="font-semibold">Error loading reconciliation data</p>
            <p className="text-sm mt-1">{errorReconciliation.message}</p>
          </div>
        </div>
      )
    }
  }

  const stopPropagation = (event: MouseEvent) => { event.stopPropagation() }

  const handleOpenReconciliationPopup = () => {
    setIsReconcilePopupOpen(true)
  }

  const handleCloseReconciliationPopup = () => {
    setIsReconcilePopupOpen(false)
    setSelectedRecordIndex(0)
  }

  // Common helpers to avoid duplicated logic while preserving behavior
  const canShowPreApprove = (currentStatus: string): boolean => {
    return (ableToProceed || (currentStatus === 'ASSET-READY' && reconcileIndicator === 1)) && !hasDataChanges
  }

  const renderSaveOrRejectButtons = () => (
    hasDataChanges
      ? (
        <ActionButton
          primaryLabel="Save"
          secondaryLabel="Cancel"
          primaryAction={handleSaveRecalc}
          secondaryAction={handleCancelClick}
          primaryLoading={isMutating}
          secondaryLoading={false}
          primaryDisabled={false}
        />
        )
      : (
        <ActionButton
          primaryLabel="Reject"
          secondaryLabel="Close"
          primaryAction={handleRejectClick}
          secondaryAction={handleCancelClick}
          primaryLoading={isSecondaryMutating}
          secondaryLoading={false}
          primaryButtonColor="bg-red-600 hover:bg-red-700 text-white"
          primaryDisabled={false}
        />
        )
  )

  return (
    <>
    <div className="fixed z-50 inset-0 overflow-auto flex justify-center items-center">
      {isConfirmationDialogActive && (
        <div className="fixed inset-0 bg-black bg-opacity-50"></div>
      )}
      <div className="fixed inset-0 transition-opacity bg-gray-500 opacity-75" aria-hidden="true" />
      <div onClick={stopPropagation} className="bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:max-w-[70%] mx-auto w-auto z-5 max-h-[80%] h-auto">
        <div className='flex flex-row justify-between'>
            <button onClick={handleCloseIconClick} className="transition duration-150 ease-in-out pt-4 pl-4 rounded-full">
              <img src={closeIcon} alt='Close' width={24} height={24}/>
            </button>
            <div className='flex-grow mx-4 overflow-hidden flex justify-center items-center'>
              {isAssetLoaded && basketInstrumentIssue
                ? (
                <div className="overflow-auto whitespace-pre-wrap break-words">
                  <Message message={basketInstrumentIssue} color="red" persistent clearMessage={() => {}} />
                </div>
                  )
                : recalcMessage && (
                <div className="overflow-auto whitespace-pre-wrap break-words">
                  <Message message={recalcMessage} color={recalcMessageColor} clearMessage={() => { setRecalcMessage('') }} />
                </div>
                )}
            </div>
        </div>
        <div className="overflow-auto max-h-[20vh] text-xs md:text-base px-2 max-w-fit md:overflow-visible">
          <InputBox
            fields={TRADER_INPUT_FIELDS_MAP(assetType)}
            inputData={inputData}
            setInputData={setInputData}
            isDataReady={isDataReady}
            setMessage={setRecalcMessage}
            setMessageColor={setRecalcMessageColor}
            readOnly={!!portfolioReadOnly || status === 'CPTY-APPROVED' || isAssetLoaded}
            setHasFlag={setHasFlag}
            holidayAsWarning={holidayAsWarning}
            setDateException={setDateException} />
          </div>
          <div className="overflow-x-auto max-h-[45vh]">
            <div className="p-4">
              <RecordTable
                records={records}
                setRecords={setRecords}
                onRecordRemove={handleRecordRemoval}
                editableFields={editableField}
                isDataReady={isDataReady}
                showEdit={!portfolioReadOnly && status !== 'CPTY-APPROVED' && !isAssetLoaded}
                showRemove={!portfolioReadOnly && status !== 'CPTY-APPROVED' && !isAssetLoaded}
                setLocalNotional={setLocalNotional}
                setLocalQuantity={setLocalQuantity}
                isInEditMode={isInEditMode}
                setIsInEditMode={setIsInEditMode}
                assetType={assetType}
                currency={basketInstrumentCurrency}
              />
              <div className='flex flex-col xl:flex-row space-y-2.5 xl:space-y-0 xl:space-x-2.5 h-full'>
                <div className="flex flex-1 min-w-0">
                  <ShowNotes data={displayingNotes} />
                </div>
                <div className="flex flex-1 min-w-0">
                  <MiniNotepad
                    noteText={traderNote}
                    setNoteText={(newText) => { setTraderNote(newText) }}
                    disabled={disableEdit}
                  />
                </div>
                <div className='flex flex-1 flex-col space-y-2.5 xl:space-y-0 xl:flex-row pt-2 min-w-fit'>
                  <div className="flex">
                    Notional MV:
                    <div className='ml-1 mr-2'>
                      <span className='bg-red-100 pl-2 pr-2'>{formatInputToDollar(localNotional, { prefix: getCurrencyPrefix(currency) })}</span>
                    </div>
                  </div>
                  <div className="flex">
                    Total Quantity:
                    <div className='ml-1 mr-2'>
                      <span className='bg-red-100 pl-2 pr-2'>{formatInputToDollar(localQuantity, { digit: 0 })}</span>
                    </div>
                  </div>
                  <div className="flex">
                    Num of assets:
                    <div className='ml-1'>
                      <span className='bg-yellow-100 pl-2 pr-2'>
                        {records.filter(record => !record.isRemoved).length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center p-4 pt-0 overflow-hidden">
          {!isRejected
            ? (
            <div className="flex flex-auto space-x-4 items-center">
              {(status !== 'CPTY-APPROVED') && (reconcileIndicator === 1 || reconcileIndicator === 4 || reconcileIndicator === 6 || reconcileIndicator === 7 || reconcileIndicator === 8 || reconcileIndicator === 9) && reconcileCount > 0
                ? (
                <div className="flex flex-col">
                  <button onClick={handleOpenReconciliationPopup} className="text-red-500 underline text-left mx-2">
                    Reconcile Details: {reconcileCount}
                  </button>
                  {reconcileStatus === '#WARNING#' && (
                    <div className="mt-1 px-2 py-1 bg-yellow-100 rounded">
                      Reconciled Portfolio came with a slightly different Notional amount within the allowed threshold
                    </div>
                  )}
                </div>
                  )
                : (status === 'CPTY-APPROVED' || reconcileIndicator === 5 || reconcileIndicator === 0 || reconcileIndicator === 8)
                    ? (
                        disableEdit
                          ? (
                         <div className={`${fundingRequestCode ? 'visible' : 'invisible'} flex flex-1 p-5`}>
                           <div className="flex items-center mr-4">
                             Inter-company Loan:
                             <div className="ml-1 pl-2 pr-2 mr-2 bg-lime-200">
                               {fundingRequestCode}
                             </div>
                           </div>
                           <div className={`${execScheme ? 'visible' : 'invisible'} flex items-center`}>
                             Execution Scheme:
                             <div className="ml-1 pl-2 pr-2 bg-lime-200">
                               {selectedExecScheme}
                             </div>
                           </div>
                         </div>
                            )
                          : (
                         <>
                           <GenericDropdown
                             options={FRDropDownOptions.map(fr => fr.FundingRequestCode)}
                             selectedValue={selectedFRCode ?? undefined}
                             onChange={handleDropdownChange}
                             displayMsg={'Select an Intercompany Loan'}
                             className="w-64"
                           />
                           {execScheme && (
                             <GenericDropdown
                               options={execSchemeOptions.map(s => s.ExecutionScheme)}
                               selectedValue={selectedExecScheme ?? undefined}
                               onChange={handleExecSchemeChange}
                               displayMsg={'Select Execution Scheme'}
                               className="w-64"
                             />
                           )}
                         </>
                            )
                      )
                    : null}
            </div>
              )
            : (
            <div className="flex flex-auto"></div>
              )}
          <div className="flex flex-shrink-0 justify-end">
          {isAssetLoaded && (
            <div className="px-4 py-3 sm:px-6">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 text-base font-medium bg-white text-gray-700 hover:bg-gray-50 focus:outline-none sm:w-auto sm:text-sm transition-colors duration-150"
                onClick={handleCancelClick}
              >
                Close
              </button>
            </div>
          )}
          {status === 'CPTY-APPROVED'
            ? hasDataChanges
              ? (
            <ActionButton
              primaryLabel="Save"
              secondaryLabel="Cancel"
              primaryAction={handleSaveRecalc}
              secondaryAction={handleCancelClick}
              primaryDisabled={false}
              primaryLoading={isMutating}
              secondaryLoading={false}
            />
                )
              : (
            <ActionButton
              primaryLabel="Reject"
              secondaryLabel="Close"
              primaryAction={handleRejectClick}
              secondaryAction={handleCancelClick}
              primaryDisabled={false}
              primaryLoading={isSecondaryMutating}
              secondaryLoading={false}
              primaryButtonColor="bg-red-600 hover:bg-red-700 text-white"
            />
                )
            : shouldShowRejectButton
              ? (
            <ActionButton
              primaryLabel="Reject"
              secondaryLabel="Close"
              primaryAction={handleRejectClick}
              secondaryAction={handleCancelClick}
              primaryDisabled={false}
              primaryLoading={isSecondaryMutating}
              secondaryLoading={false}
              primaryButtonColor="bg-red-600 hover:bg-red-700 text-white"
            />
                )
              : isPreApproved
                ? (
            <ActionButton
              primaryLabel="Reject"
              secondaryLabel="Close"
              primaryAction={handleRejectClick}
              secondaryAction={handleCancelClick}
              primaryDisabled={false}
              primaryLoading={isSecondaryMutating}
              secondaryLoading={false}
              primaryButtonColor="bg-red-600 hover:bg-red-700 text-white"
            />
                  )
                : status === 'RISK-APPROVED-EXCEPTION' && !disableEdit
                  ? (
            <ActionButton
              primaryLabel="Link Intercompany Loan"
              secondaryLabel="Cancel"
              primaryAction={handleSaveRecalc}
              secondaryAction={handleCancelClick}
              primaryDisabled={!hasDataChanges}
              primaryLoading={isMutating}
              secondaryLoading={false}
            />
                    )
                  : status === 'RISK-APPROVED-EXCEPTION' && disableEdit
                    ? (
            <ActionButton
              primaryLabel="Cancel"
              secondaryLabel=""
              primaryAction={handleCancelClick}
              secondaryAction={async () => {}}
              primaryDisabled={false}
              primaryLoading={false}
              secondaryLoading={false}
            />
                      )
                    : !disableEdit && (
                        // Show when not rejected as before
                        !isRejected || shouldShowPreApproveButtons ||
                        // Always show proceed block for non-recon and reconciled cases
                        reconcileIndicator === 5 || reconcileIndicator === 0 ||
                        // After TRADER-REJECTED, show proceed for 7 and 8 (exception paths)
                        (status === 'TRADER-REJECTED' && (reconcileIndicator === 7 || reconcileIndicator === 8))
                      ) && !isPreApproved && status !== 'TRADER-APPROVED' && status !== 'RISK-APPROVED' && status !== 'TRADER-APPROVED-EXCEPTION' && status !== 'RISK-APPROVED-EXCEPTION'
                        ? (
              <>
                {shouldShowPreApproveException && canShowPreApprove(status)
                  ? (
                  <ActionButton
                    primaryLabel="Pre-Approve-Exception"
                    secondaryLabel={status === 'TRADER-REJECTED' ? 'Close' : 'Reject'}
                    primaryAction={async () => {
                      await handlePrimaryActionWithValidation('TRADER-PREAPPROVED-EXCEPTION')
                    }}
                    secondaryAction={status === 'TRADER-REJECTED' ? handleCancelClick : handleRejectClick}
                    primaryDisabled={
                      reconcileStatus === '#ERROR#' || (
                        (
                          !ableToProceed && !dataChanged &&
                          !(status === 'ASSET-READY' && reconcileIndicator === 1) &&
                          !isRejected
                        ) || isInEditMode
                      )
                    }
                    primaryLoading={isMutating}
                    secondaryLoading={isSecondaryMutating}
                    primaryButtonColor="bg-green-600 hover:bg-green-700 text-white"
                    secondaryButtonColor={status === 'TRADER-REJECTED' ? undefined : 'bg-red-600 hover:bg-red-700 text-white'}
                  />
                    )
                  : shouldShowPreApproveButtons && canShowPreApprove(status)
                    ? (
                  <ActionButton
                    primaryLabel="Pre-Approve"
                    secondaryLabel={status === 'TRADER-REJECTED' ? 'Close' : 'Reject'}
                    primaryAction={async () => {
                      await handlePrimaryActionWithValidation('TRADER-PREAPPROVED')
                    }}
                    secondaryAction={status === 'TRADER-REJECTED' ? handleCancelClick : handleRejectClick}
                    primaryDisabled={
                      reconcileStatus === '#ERROR#' || (
                        (
                          !ableToProceed && !dataChanged &&
                          !(status === 'ASSET-READY' && reconcileIndicator === 1) &&
                          !isRejected
                        ) || isInEditMode
                      )
                    }
                    primaryLoading={isMutating}
                    secondaryLoading={isSecondaryMutating}
                    secondaryButtonColor={status === 'TRADER-REJECTED' ? undefined : 'bg-red-600 hover:bg-red-700 text-white'}
                  />
                      )
                    : (
                  <ActionButton
                    primaryLabel={getPrimaryLabel}
                    secondaryLabel={status === 'TRADER-REJECTED' ? 'Close' : 'Reject'}
                    primaryAction={async () => {
                      // Determine custom status based on reconcileIndicator and riskOverall
                      const customStatus =
                        reconcileIndicator === 1
                          ? ((riskOverall && !dateException) ? 'TRADER-PREAPPROVED' : 'TRADER-PREAPPROVED-EXCEPTION')
                          : (reconcileIndicator === 8 || !riskOverall || dateException)
                              ? 'TRADER-APPROVED-EXCEPTION'
                              : 'TRADER-APPROVED'
                      await handlePrimaryActionWithValidation(customStatus)
                    }}
                    secondaryAction={status === 'TRADER-REJECTED' ? handleCancelClick : handleRejectClick}
                    primaryDisabled={
                      reconcileStatus === '#ERROR#' || (
                        (
                          !ableToProceed && !dataChanged &&
                          !(status === 'ASSET-READY' && reconcileIndicator === 1) &&
                          !(status === 'ASSET-READY' && reconcileIndicator === 0) &&
                          // Allow proceed for risk-preapproved reconcile 5 or 8 even if ableToProceed is false
                          !(status === 'RISK-PREAPPROVED' && (reconcileIndicator === 5 || reconcileIndicator === 8)) &&
                          !(status === 'RISK-PREAPPROVED-EXCEPTION' && (reconcileIndicator === 5 || reconcileIndicator === 8)) &&
                          !isRejected
                        ) || isInEditMode
                      )
                    }
                    primaryLoading={isMutating}
                    secondaryLoading={isSecondaryMutating}
                    secondaryButtonColor={status === 'TRADER-REJECTED' ? undefined : 'bg-red-600 hover:bg-red-700 text-white'}
                  />
                      )}
              </>
                          )
                        : status === 'TRADER-APPROVED' && !disableEdit
                          ? renderSaveOrRejectButtons()
                          : status === 'TRADER-APPROVED-EXCEPTION' && !disableEdit
                            ? renderSaveOrRejectButtons()
                            : status === 'RISK-APPROVED' && !disableEdit
                              ? renderSaveOrRejectButtons()
                              : null}
          </div>
          </div>
        </div>
      </div>

      {isReconcilePopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="overflow-auto bg-white rounded-lg shadow-lg w-3/4 h-3/4 p-4 relative max-h-fit">
          <div className='flex justify-between p-4'>
            <button onClick={handleCloseReconciliationPopup} className="transition duration-150 ease-in-out rounded-full">
              <img src={closeIcon} alt='Close' width={24} height={24}/>
            </button>
            <h2 className="text-xl font-bold text-justify">Reconciliation Details</h2>
            <ReconcileSummaryList
              resultData={reconciliationResultData?.summaryData ?? []}
              setSelectedRecordIndex={setSelectedRecordIndex}
              selectedRecordIndex={selectedRecordIndex}
            />
          </div>
            <ReconcileDetails
              summaryData={reconciliationResultData?.summaryData[selectedRecordIndex]}
              detailsData={reconciliationResultData?.detailsData}
            />
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={isConfirmationDialogActive}
        onClose={handleDiscard}
        onConfirm={async () => { setIsConfirmationDialogActive(false) }}
        onCancel={async () => { setIsConfirmationDialogActive(false) }}
        question="You have unsaved changes!"
        title="Unsaved Changes"
        confirmText="Go Back"
        cancelText="Discard"
        primaryLoading={isMutating}
        secondaryLoading={isSecondaryMutating}
      />
      <ConfirmationModal
        isOpen={isRemoveRecordModalOpen}
        onCancel={async () => { setIsRemoveRecordModalOpen(false) }}
        onConfirm={confirmRecordRemoval}
        question="Are you sure you want to remove this record?"
        title="Please Confirm"
        confirmText="Confirm"
        cancelText="Go Back"
        confirmButtonColor='bg-red-600 hover:bg-red-700 focus:ring-red-500'
      />
    </>
  )
}

export default React.memo(AssetBucketsPopUp)
